import React from "react";
import { buildGoalsDomain } from "../../../features/profileContract";
import { restoreProfileGoal } from "../../../services/profileGoalService";

const getGoals = (supportProfile = {}) => (
  Array.isArray(supportProfile?.careGoals) ? supportProfile.careGoals : []
);

const useProfileGoalManagement = ({
  child,
  goalsChapter,
  canEditProfile,
}) => {
  const [supportProfileOverride, setSupportProfileOverride] = React.useState(null);
  const [selectedGoal, setSelectedGoal] = React.useState(null);
  const [removedGoal, setRemovedGoal] = React.useState(null);
  const [message, setMessage] = React.useState("");

  React.useEffect(() => {
    setSupportProfileOverride(null);
    setSelectedGoal(null);
    setRemovedGoal(null);
    setMessage("");
  }, [child?.id]);

  const supportProfile = supportProfileOverride || child?.supportProfile || {};
  const childForGoalWrites = React.useMemo(() => ({
    ...(child || {}),
    supportProfile,
  }), [child, supportProfile]);

  const resolvedGoalsChapter = React.useMemo(() => (
    supportProfileOverride
      ? buildGoalsDomain({ child: childForGoalWrites })
      : goalsChapter
  ), [childForGoalWrites, goalsChapter, supportProfileOverride]);

  const openGoal = React.useCallback((entry) => {
    if (!canEditProfile) return;
    const goalId = entry?.sourceAction?.actionId;
    const goal = getGoals(supportProfile).find((item) => item.id === goalId);
    if (goal) setSelectedGoal(goal);
  }, [canEditProfile, supportProfile]);

  const applySupportProfile = React.useCallback((nextSupportProfile) => {
    setSupportProfileOverride(nextSupportProfile);
    setSelectedGoal((current) => (
      current
        ? getGoals(nextSupportProfile).find((goal) => goal.id === current.id) || null
        : null
    ));
  }, []);

  const handleRemoved = React.useCallback(({ goal, nextSupportProfile }) => {
    setSupportProfileOverride(nextSupportProfile);
    setRemovedGoal(goal);
    setMessage("Goal removed");
  }, []);

  const undoRemove = React.useCallback(async () => {
    if (!removedGoal) return;
    try {
      const nextSupportProfile = await restoreProfileGoal({
        childId: child?.id,
        childName: child?.name,
        supportProfile,
        goal: removedGoal,
      });
      setSupportProfileOverride(nextSupportProfile);
      setRemovedGoal(null);
      setMessage("Goal restored");
    } catch (error) {
      setMessage(error?.message || "Couldn't restore this goal.");
    }
  }, [child?.id, child?.name, removedGoal, supportProfile]);

  return {
    childForGoalWrites,
    resolvedGoalsChapter,
    selectedGoal,
    closeGoal: () => setSelectedGoal(null),
    openGoal,
    applySupportProfile,
    handleRemoved,
    message,
    clearMessage: () => setMessage(""),
    canUndoRemove: Boolean(removedGoal),
    undoRemove,
  };
};

export default useProfileGoalManagement;
